import { FunctionComponent, ComponentProps, ReactNode } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import RecaptchaTerms from 'dibs-recaptcha/exports/RecaptchaTerms';
import getBuyerSupportLink from 'dibs-support-link/exports/getBuyerSupportLink';
import { SeoLink } from 'dibs-elements/exports/SeoLink';
import classnames from 'classnames';

import { trackCheckoutFooterNavigationClick } from './tracking';
import { useCheckoutSelector } from '../reducers/root';
import {
    isEnhancedBuyerIncentivesVariant,
    isBuyerIncentivesDesktopVariant2,
    isBuyerIncentivesDesktopVariant1,
} from '../helpers/isEnhancedBuyerIncentivesVariant';
import { BuyerIncentives } from '../components/shared/BuyerIncentives';

import { CheckoutFooter_viewer$key } from './__generated__/CheckoutFooter_viewer.graphql';
import { CheckoutFooter_transaction$key } from './__generated__/CheckoutFooter_transaction.graphql';
import { CheckoutFooter_buyer$key } from './__generated__/CheckoutFooter_buyer.graphql';
import { CheckoutFooter_item$key } from './__generated__/CheckoutFooter_item.graphql';

import dibsCss from 'dibs-css';
import styles from './styles/CheckoutFooter.scss';

type FooterLink = Pick<ComponentProps<typeof SeoLink>, 'target' | 'dataTn' | 'onClick'> & {
    name: ReactNode;
    href: string;
};

const supportLink: FooterLink = {
    name: <FormattedMessage id="checkout.CheckoutFooter.support" defaultMessage="Support Center" />,
    href: getBuyerSupportLink(''),
    target: '_blank',
    dataTn: 'simple-footer-support',
    onClick: (): void => trackCheckoutFooterNavigationClick({ label: 'Support Center' }),
};

const footLinks: FooterLink[] = [
    {
        name: <FormattedMessage id="checkout.CheckoutFooter.privacy" defaultMessage="Privacy" />,
        href: '/about/privacy-policy/',
        target: '_blank',
        dataTn: 'simple-footer-privacy',
        onClick: (): void => trackCheckoutFooterNavigationClick({ label: 'Privacy' }),
    },
    {
        name: <FormattedMessage id="checkout.CheckoutFooter.terms" defaultMessage="Terms" />,
        href: '/about/user-agreement/',
        target: '_blank',
        dataTn: 'simple-footer-terms',
        onClick: (): void => trackCheckoutFooterNavigationClick({ label: 'Terms' }),
    },
];

const transactionFragment = graphql`
    fragment CheckoutFooter_transaction on Transaction {
        buyerChargeCurrency
    }
`;

const viewerFragment = graphql`
    fragment CheckoutFooter_viewer on Viewer {
        merchantAccounts(accountType: TRANSACTION) {
            countryName
            currency
        }
        ...BuyerIncentives_viewer
    }
`;

const buyerFragment = graphql`
    fragment CheckoutFooter_buyer on User {
        ...BuyerIncentives_buyer
    }
`;

const itemFragment = graphql`
    fragment CheckoutFooter_item on Item {
        ...BuyerIncentives_item
    }
`;

type Props = {
    isMobile: boolean;
    viewer: CheckoutFooter_viewer$key | null;
    transaction: CheckoutFooter_transaction$key | null;
    buyer: CheckoutFooter_buyer$key | null;
    item: CheckoutFooter_item$key | null;
};

export const CheckoutFooter: FunctionComponent<Props> = props => {
    const { isMobile } = props;

    const transaction = useFragment(transactionFragment, props.transaction);
    const viewer = useFragment(viewerFragment, props.viewer);
    const buyer = useFragment(buyerFragment, props.buyer);
    const item = useFragment(itemFragment, props.item);

    const isEnhancedBuyerIncentivesClientVariant = isEnhancedBuyerIncentivesVariant() && isMobile;
    const isBuyerIncentivesDesktopClientVariant2 = isBuyerIncentivesDesktopVariant2() && !isMobile;
    const isBuyerIncentivesDesktopVariant =
        (isBuyerIncentivesDesktopVariant1() || isBuyerIncentivesDesktopClientVariant2) && !isMobile;

    const checkoutHelpType = useCheckoutSelector(state => state.ui.checkoutHelpType);

    const merchantAccount = (viewer?.merchantAccounts || []).find(
        account => account?.currency === transaction?.buyerChargeCurrency
    );

    return (
        <footer
            id="footer"
            className={
                isBuyerIncentivesDesktopClientVariant2 ? 'primary-viewport-width' : undefined
            }
        >
            {isBuyerIncentivesDesktopClientVariant2 && !isMobile && (
                <div
                    className={classnames(
                        styles.buyerIncentives,
                        dibsCss.flex,
                        dibsCss.itemsCenter,
                        dibsCss.justifyCenter,
                        dibsCss.gapXlarge,
                        dibsCss.borderT,
                        dibsCss.borderTSolid,
                        dibsCss.borderTDolphin
                    )}
                >
                    {viewer && item && (
                        <BuyerIncentives
                            viewer={viewer}
                            item={item}
                            buyer={buyer}
                            incentiveClassName={classnames(
                                dibsCss.flex,
                                dibsCss.sassyFontHeaderSmall
                            )}
                            iconWrapperClassName={dibsCss.fillShagreen400}
                        />
                    )}
                </div>
            )}
            {!(isEnhancedBuyerIncentivesClientVariant || isBuyerIncentivesDesktopVariant) && (
                <div className={styles.buyerAssuranceWrapper}>
                    <div className={styles.buyerAssurance}>
                        <FormattedMessage
                            id="checkout.CheckoutFooter.buyerProtection"
                            defaultMessage="Buyer Protection Guaranteed"
                        />
                    </div>
                    <div className={styles.buyerAssurance}>
                        <FormattedMessage
                            id="checkout.CheckoutFooter.vettedSellers"
                            defaultMessage="Only Expertly Vetted Sellers"
                        />
                    </div>
                    <div className={styles.buyerAssurance}>
                        <FormattedMessage
                            id="checkout.CheckoutFooter.globalDelivery"
                            defaultMessage="100% Insured Global Delivery"
                        />
                    </div>
                </div>
            )}
            <div className={`${styles.footer} boxSizingWrapper`}>
                <nav className={`${styles.menuRow} cf`}>
                    <ul className={styles.links}>
                        {(checkoutHelpType === 'GET_HELP'
                            ? [supportLink, ...footLinks]
                            : footLinks
                        ).map(f => (
                            <li key={f.dataTn} className={styles.menuItem} data-tn={f.dataTn}>
                                <SeoLink
                                    className={styles.link}
                                    dataTn={f.dataTn + '-link'}
                                    linkData={{ path: f.href }}
                                    target={f.target}
                                    onClick={f?.onClick}
                                >
                                    {f.name}
                                </SeoLink>
                            </li>
                        ))}
                    </ul>
                </nav>
                {!isMobile && (
                    <div className={styles.copyright} data-tn="footer-copyright">
                        <FormattedMessage
                            id="checkout.CheckoutFooter.copyright"
                            defaultMessage="© 2001–{year}, 1stDibs.com, Inc. | {regionCodeText}"
                            values={{
                                year: new Date().getFullYear(),
                                regionCodeText: merchantAccount?.countryName,
                            }}
                        />
                    </div>
                )}
            </div>
            <RecaptchaTerms
                wrapperClass={styles.recaptcha}
                linkClass={styles.recaptchaLink}
                trackClickEvent={({ label }: { label: string }) =>
                    trackCheckoutFooterNavigationClick({ label })
                }
            />
        </footer>
    );
};
